import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, LinearProgress, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, Grid, CircularProgress, IconButton
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as SuccessIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const PstUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      const token = getAuthAccessToken();
      const response = await axios.get(`${API_URL}/email-extractor/pst/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.data || []);
    } catch (error) {
      console.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith('.pst')) {
        toast.error('Please select a PST file');
        return;
      }
      if (selectedFile.size > 500 * 1024 * 1024) {
        toast.error('File size must be less than 500MB');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/vnd.ms-outlook': ['.pst'] },
    maxFiles: 1,
    disabled: uploading
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setProgress(0);
    setResult(null);

    const formData = new FormData();
    formData.append('pstFile', file);

    try {
      const token = getAuthAccessToken();
      const response = await axios.post(`${API_URL}/email-extractor/pst/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percent);
        }
      });

      setResult(response.data.data.stats);
      toast.success(`Extracted ${response.data.data.stats.newEmailsAdded} new emails!`);
      loadHistory();
      setFile(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process PST file');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
        📁 PST File Upload
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Extract email addresses from Microsoft Outlook PST files
      </Typography>

      <Grid container spacing={3}>
        {/* Upload Area */}
        <Grid item xs={12} md={8}>
          <Paper
            {...getRootProps()}
            sx={{
              p: 4,
              textAlign: 'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : file ? 'success.main' : 'divider',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: uploading ? 'divider' : 'primary.main',
                bgcolor: uploading ? 'background.paper' : 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            
            {!file ? (
              <>
                <UploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {isDragActive ? 'Drop your PST file here' : 'Drag & drop your PST file here'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to browse
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.disabled' }}>
                  Supported: .pst files up to 500MB
                </Typography>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <FileIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight={600}>{file.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                {!uploading && (
                  <IconButton onClick={(e) => { e.stopPropagation(); clearFile(); }} size="small">
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </Paper>

          {/* Progress Bar */}
          {uploading && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Processing PST file...</Typography>
                <Typography variant="body2">{progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                <InfoIcon sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption">Large files may take several minutes to process</Typography>
              </Box>
            </Paper>
          )}

          {/* Upload Button */}
          {file && !uploading && !result && (
            <Button
              variant="contained"
              size="large"
              startIcon={<UploadIcon />}
              onClick={handleUpload}
              sx={{ mt: 2, width: '100%' }}
            >
              Start Processing
            </Button>
          )}

          {/* Result */}
          {result && (
            <Paper sx={{ p: 3, mt: 2, bgcolor: 'success.light' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SuccessIcon color="success" />
                <Typography variant="h6">Extraction Complete</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="h4" color="primary">{result.messagesProcessed}</Typography>
                      <Typography variant="caption">Messages Processed</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="h4" color="primary">{result.totalEmailsFound}</Typography>
                      <Typography variant="caption">Unique Emails</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="h4" color="success.main">{result.newEmailsAdded}</Typography>
                      <Typography variant="caption">New Added</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="h4" color="text.secondary">{result.duplicateEmails}</Typography>
                      <Typography variant="caption">Duplicates</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Button variant="outlined" onClick={clearFile} sx={{ mt: 2 }}>
                Upload Another File
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Help Section */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="info" /> About PST Files
            </Typography>
            <Typography variant="body2" paragraph>
              PST (Personal Storage Table) files are used by Microsoft Outlook to store emails, 
              contacts, and other data. You can export your emails to a PST file from Outlook 
              and upload it here to extract email addresses.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>How to export PST from Outlook:</Typography>
            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              <li><Typography variant="body2">Open Outlook and go to File → Open & Export → Import/Export</Typography></li>
              <li><Typography variant="body2">Select "Export to a file" and click Next</Typography></li>
              <li><Typography variant="body2">Choose "Outlook Data File (.pst)" and click Next</Typography></li>
              <li><Typography variant="body2">Select the folder you want to export and save the file</Typography></li>
            </Box>
          </Paper>
        </Grid>

        {/* History */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> Upload History
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : history.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No upload history yet
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Reference</TableCell>
                      <TableCell>Filename</TableCell>
                      <TableCell align="center">Messages</TableCell>
                      <TableCell align="center">Emails Found</TableCell>
                      <TableCell align="center">New Added</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{item.reference_number}</Typography>
                        </TableCell>
                        <TableCell>{item.filename}</TableCell>
                        <TableCell align="center">{item.messages_processed || 0}</TableCell>
                        <TableCell align="center">{item.total_emails_found || 0}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.new_emails_added || 0} 
                            size="small" 
                            color={item.new_emails_added > 0 ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.status} 
                            size="small" 
                            color={item.status === 'completed' ? 'success' : item.status === 'failed' ? 'error' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>{formatDate(item.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PstUpload;
